import React from "react";

import Constants from "../../constants";
import { RotationOptionDTO, RotationScheduleDTO } from "../../models/RotationDTO";

export interface RotationPickerProps {
	homeSchedule: RotationScheduleDTO | null;
	options: RotationOptionDTO[];
	rotating: boolean;
	scheduleIds: number[];
	onChange: (rotating: boolean, scheduleIds: number[]) => void;
	disabled?: boolean;
	loading?: boolean;
	/** Unique prefix for the input ids when several pickers live on the same screen. */
	idPrefix?: string;
}

/** Hours read as "4 h", "1.5 h". */
export const horasTexto = (horas: number | null | undefined): string => {
	const valor = Number(horas ?? 0);
	return `${Number.isInteger(valor) ? valor : valor.toFixed(1)} h`;
};

/**
 * Compact "Rotativo" block (Esteban, 2026-09-17): a checkbox with one line under it and, when checked,
 * the sibling schedules (same módulo + periodo) the student may also attend. The weekly cap is the home
 * schedule's hours; the server enforces it when attendance is registered.
 */
const RotationPicker: React.FC<RotationPickerProps> = ({ homeSchedule, options, rotating, scheduleIds, onChange, disabled, loading, idPrefix }) => {
	const prefix = idPrefix || "rotation";
	const sinOpciones = !loading && options.length < 1;
	const bloqueado = disabled || loading || sinOpciones;

	const toggleSchedule = (scheduleId: number, checked: boolean) => {
		const siguientes = checked
			? [...scheduleIds.filter((id) => id !== scheduleId), scheduleId]
			: scheduleIds.filter((id) => id !== scheduleId);
		onChange(true, siguientes);
	};

	return (
		<div style={{ background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderRadius: 6, padding: "10px 14px" }}>
			<div className="form-check" style={{ marginBottom: 0 }}>
				<input id={`${prefix}_rotating`} className="form-check-input" type="checkbox" checked={rotating && !sinOpciones} disabled={bloqueado}
					onChange={(event) => onChange(event.target.checked, event.target.checked ? scheduleIds : [])} />
				<label htmlFor={`${prefix}_rotating`} className="form-check-label" style={{ fontWeight: 600 }}>
					{Constants.Common.rotating}
				</label>
			</div>
			<div style={{ fontSize: 13, color: "var(--wa-apagado)", marginLeft: 24 }}>
				{loading
					? Constants.Common.loading
					: (sinOpciones
						? Constants.Common.noSiblingSchedules
						: Constants.Common.rotatingHint.replace("{weeklyHours}", horasTexto(homeSchedule?.weeklyHours)))}
			</div>
			{rotating && !sinOpciones ? (
				<div style={{ marginLeft: 24, marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
					{options.map((option) => (
						<div key={`${prefix}_${option.id}`} className="form-check" style={{ marginBottom: 0 }}>
							<input id={`${prefix}_s_${option.id}`} className="form-check-input" type="checkbox" disabled={disabled}
								checked={scheduleIds.includes(option.id)} onChange={(event) => toggleSchedule(option.id, event.target.checked)} />
							<label htmlFor={`${prefix}_s_${option.id}`} className="form-check-label" style={{ fontSize: 13 }}>
								{option.name}
								{option.mentorName ? <span style={{ color: "var(--wa-apagado)" }}> · {option.mentorName}</span> : ""}
								<span style={{ color: "var(--wa-apagado)" }}> · {horasTexto(option.weeklyHours)}</span>
							</label>
						</div>
					))}
				</div>
			) : ""}
		</div>
	);
};

export default RotationPicker;
